import * as React from 'react'
import styled from 'styled-components'

interface IProps {
  children: string
  color?: string
  linkColor?: string
  // trueの場合、素のURL（http(s)://〜）もリンク化する（デフォルトは従来通りMarkdownリンクのみ）
  autoLinkUrls?: boolean
}

// [title](https://...) 形式のMarkdownリンク
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g
// 素のURL（RFC 3986 の許容文字のホワイトリスト方式。
// 日本語文字等はURLに含めない＝「URL直後に区切りなしで日本語が続く」文章で本文を巻き込まない。
// URL内の日本語はブラウザからのコピーで %エンコードされる前提で `%` により対応）
const BARE_URL_REGEX = /https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/g
// URL末尾に食い込みがちな句読点・閉じ括弧（`.` `)` 等はURL中に正当に現れるため、マッチ後に末尾からのみ剥がす）
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)]+$/

interface ITextPart {
  type: 'text'
  value: string
}
interface ILinkPart {
  type: 'link'
  title: string
  url: string
}

type Part = ITextPart | ILinkPart

// テキスト中の素のURLをリンクパートに分解する
const parseBareUrlParts = (text: string): Part[] => {
  const parts: Part[] = []
  let lastIndex = 0

  BARE_URL_REGEX.lastIndex = 0
  for (let match = BARE_URL_REGEX.exec(text); match !== null; match = BARE_URL_REGEX.exec(text)) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) })
    }
    const trailing = match[0].match(TRAILING_PUNCTUATION_REGEX)?.[0] ?? ''
    const url = match[0].slice(0, match[0].length - trailing.length)
    parts.push({ type: 'link', title: url, url })
    if (trailing) {
      parts.push({ type: 'text', value: trailing })
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) })
  }
  return parts
}

// Markdownリンクを先にパースし、autoLinkUrls時のみ残りのテキストから素のURLをパースする
const parseParts = (text: string, autoLinkUrls: boolean): Part[] => {
  const parts: Part[] = []
  let lastIndex = 0

  const pushText = (value: string) => {
    if (autoLinkUrls) {
      parts.push(...parseBareUrlParts(value))
    } else {
      parts.push({ type: 'text', value })
    }
  }

  MARKDOWN_LINK_REGEX.lastIndex = 0
  for (
    let match = MARKDOWN_LINK_REGEX.exec(text);
    match !== null;
    match = MARKDOWN_LINK_REGEX.exec(text)
  ) {
    if (match.index > lastIndex) {
      pushText(text.slice(lastIndex, match.index))
    }
    parts.push({ type: 'link', title: match[1], url: match[2] })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    pushText(text.slice(lastIndex))
  }
  return parts
}

const LinkableText: React.FC<IProps> = ({ children, color, linkColor, autoLinkUrls = false }) => {
  return (
    <span style={{ color }}>
      {parseParts(children, autoLinkUrls).map((part, i) =>
        part.type === 'link' ? (
          <Anchor
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            $linkColor={linkColor}
          >
            {part.title}
          </Anchor>
        ) : (
          part.value
        )
      )}
    </span>
  )
}

const Anchor = styled.a<{ $linkColor?: string }>`
  color: ${({ $linkColor }) => $linkColor ?? 'inherit'};
  text-decoration: underline;
  &:hover {
    opacity: 0.7;
  }
`

export default LinkableText
